// src/components/partner/AccountInformationCard.tsx

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Refresh, Tune, Visibility, VisibilityOff } from '@mui/icons-material';

import { copyToClipboard } from '@utils/common';
import type { Partner, PartnerForm } from '@models/partner';

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

type Props = {
  form: Partner;
  highlighted?: boolean;
  onChangeInput: (name: keyof PartnerForm, value: string | boolean | number | null) => void;
  onRegeneratePassword: () => void;
  sectionWrapper: (args: SectionWrapperArgs) => React.ReactElement;
};

const PASSWORD_HELP_TEXT = 'Credentials are rotated only by Regenerate. Copy and share securely.';
const INPUT_HEIGHT_SM = 40; // MUI v5 small Outlined input height
const ACTION_COL_WIDTH_MD = 180;

const MONO_SX = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  textOverflow: 'ellipsis',
} as const;

/**
 * Account Information card
 * - Account ID is read-only (copyable)
 * - Password is read-only (show/copy) and rotated via Regenerate
 * - Profile fields are editable
 *
 * Layout note:
 * The password TextField uses helperText, which increases total height.
 * To keep the adjacent Regenerate button aligned, the button column reserves
 * the same helperText space via an invisible caption line.
 */
const AccountInformationCard = ({
  form,
  highlighted,
  onChangeInput,
  onRegeneratePassword,
  sectionWrapper,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const closeToast = useCallback((_: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setToastMessage(null);
  }, []);

  const copyAndToast = useCallback((value: string, message: string) => {
    copyToClipboard(value);
    setToastMessage(message);
  }, []);

  const username = form.managerUsername ?? '';
  const password = form.managerPassword ?? '';

  const helperTextNode = useMemo(
    () => (
      <Typography variant="caption" color="text.secondary">
        {PASSWORD_HELP_TEXT}
      </Typography>
    ),
    [],
  );

  const helperTextSpacer = useMemo(
    () => (
      <Typography
        variant="caption"
        sx={{
          mt: '3px',
          lineHeight: 1.66,
          visibility: 'hidden',
        }}
      >
        {PASSWORD_HELP_TEXT}
      </Typography>
    ),
    [],
  );

  const actionColSx = useMemo(
    () => ({
      flexShrink: 0,
      minWidth: { xs: '100%', md: ACTION_COL_WIDTH_MD },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    }),
    [],
  );

  return sectionWrapper({
    title: 'Account Information',
    icon: <Tune fontSize="small" />,
    highlighted,
    children: (
      <>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <TextField
            label="Account ID"
            value={username}
            size="small"
            fullWidth
            disabled
            sx={{ '& .MuiInputBase-input': MONO_SX }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => copyAndToast(username, 'Account ID copied')}
                      aria-label="Copy Account ID"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {/* Password + Regenerate (height aligned incl. helperText) */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Account Password"
                value={password}
                size="small"
                fullWidth
                disabled
                type={showPassword ? 'text' : 'password'}
                helperText={helperTextNode}
                sx={{ '& .MuiInputBase-input': MONO_SX }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => copyAndToast(password, 'Password copied')}
                          aria-label="Copy password"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={actionColSx}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={onRegeneratePassword}
                sx={{ whiteSpace: 'nowrap', height: INPUT_HEIGHT_SM }}
              >
                Regenerate
              </Button>
              {helperTextSpacer}
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
            <TextField
              label="Account Manager"
              value={form.managerName ?? ''}
              size="small"
              fullWidth
              onChange={(e) => onChangeInput('managerName', e.target.value)}
            />
            <TextField
              label="Email"
              value={form.managerEmail ?? ''}
              size="small"
              fullWidth
              onChange={(e) => onChangeInput('managerEmail', e.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
            <TextField
              label="Phone"
              value={form.managerTelephone ?? ''}
              size="small"
              fullWidth
              onChange={(e) => onChangeInput('managerTelephone', e.target.value)}
            />
            <TextField
              label="Address"
              value={form.address ?? ''}
              size="small"
              fullWidth
              onChange={(e) => onChangeInput('address', e.target.value)}
            />
          </Stack>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.manualOrderEnabled)}
                  onChange={(_, checked) => onChangeInput('manualOrderEnabled', checked)}
                />
              }
              label="Manual Order (Portal)"
            />
            <Typography variant="body2" color="text.secondary">
              Enable this to allow the partner to create orders via the partner portal (non-API).
            </Typography>
          </Box>
        </Stack>

        <Snackbar
          open={Boolean(toastMessage)}
          autoHideDuration={1800}
          onClose={closeToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeToast} severity="success" variant="filled">
            {toastMessage}
          </Alert>
        </Snackbar>
      </>
    ),
  });
};

export default AccountInformationCard;
